'use client';

import { FC, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ApplicableSubscriptionsReadOnly } from 'hotdesk_typescript';
import { collectAppliesForUnitsFromSubscriptions, hasUserApplicableSubscriptions, PRICE_UNIT_ORDER, subscriptionAppliesToUnit } from '@/hotdesk-ui/utils/listingSubscriptionUtils';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { getActiveUserSubscriptions } from '@/hotdesk-ui/utils/subscriptionUtils';
import styles from './SubscriptionPlanBanner.module.scss';
export interface SubscriptionPlanBannerProps {
  applicableSubscriptions?: ApplicableSubscriptionsReadOnly | null;
  listing?: Record<string, any>;
  priceUnit: string;
  subscription: string | null;
}
const UNIT_LABEL_KEY: Record<(typeof PRICE_UNIT_ORDER)[number], 'membershipAppliesUnit.hour' | 'membershipAppliesUnit.day' | 'membershipAppliesUnit.month' | 'membershipAppliesUnit.annual'> = {
  hour: 'membershipAppliesUnit.hour',
  day: 'membershipAppliesUnit.day',
  month: 'membershipAppliesUnit.month',
  annual: 'membershipAppliesUnit.annual'
};
const SubscriptionPlanBanner: FC<SubscriptionPlanBannerProps> = ({
  applicableSubscriptions,
  listing,
  priceUnit,
  subscription
}) => {
  const t = useTranslations('workspace-details');
  const locale = useLocale();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const activeUserSubs = useMemo(() => getActiveUserSubscriptions(user) as unknown as Record<string, unknown>[], [user]);
  const supportedUnits = useMemo(() => collectAppliesForUnitsFromSubscriptions(activeUserSubs, listing?.type), [activeUserSubs, listing?.type]);
  const unitsLabel = useMemo(() => {
    if (!supportedUnits.length) return null;
    const unitLabels = supportedUnits.map(unit => t(UNIT_LABEL_KEY[unit as (typeof PRICE_UNIT_ORDER)[number]]));
    return new Intl.ListFormat(locale, {
      style: 'long',
      type: 'conjunction'
    }).format(unitLabels);
  }, [supportedUnits, locale, t]);
  const showBanner = hasUserApplicableSubscriptions(applicableSubscriptions, listing) && (subscriptionAppliesToUnit(supportedUnits, priceUnit) || !supportedUnits.length && !!subscription);
  if (!showBanner) return null;
  return <div className={styles.infoRow} data-sentry-component="SubscriptionPlanBanner" data-sentry-source-file="SubscriptionPlanBanner.tsx">
            <span className={styles.infoIcon}>
                <img src="/assets/icons/morphy/membership-applicable.svg" alt="" className={styles.morphyIcon} aria-hidden />
            </span>
            <span className={styles.infoText}>
                <span className={styles.infoTitle}>
                    {t('membershipApplicable')}
                </span>
                {unitsLabel && <span className={styles.infoSubtitle}>
                        {t.rich('membershipAppliesTo', {
          units: unitsLabel,
          bold: chunks => <strong className={styles.unitBold}>
                                    {chunks}
                                </strong>
        })}
                    </span>}
            </span>
        </div>;
};
export default SubscriptionPlanBanner;